import { Badge, Banner, BlockStack, Box, Button, ButtonGroup, DataTable, Form, FormLayout, InlineStack, Link, Modal, Select, Text, TextField } from "@shopify/polaris";
import { useEffect, useMemo, useState } from "react";
import { getLatestOption, getRequest, postRequest, putRequest } from "../functions";
import { ModelString } from "./modelStringSelect";
import { getCodeString, getPrintLabelDetailsFromModelString, getOptions, initialLabel, initialSerial, parseModelString, saveStringInRemark, statusOptions } from "./helpers";
import { SerialSelect } from "./serialSelect";
import { useAuthStore, useLookupStore } from "../../../utils/useStore";

const EXCLUDED_AUTO_NUMBER_SERIALS = new Set(["555555", "22222222", "33333333", "44444444"]);

const RFID_CREATE_MODES = {
  OPEN_ORDER_LINES: "open-order-lines",
  ADD_NEW: "add-new",
  EXISTING: "existing",
};

const isExcludedFromAutoNumbering = (serialItem) => {
  if (!serialItem) return true;

  const serialNumber = serialItem.serialnumber?.toString();
  const remark = serialItem.remarks?.trim().toLowerCase();
  const hardwareBoiler = serialItem.hardwareBoilerVersion?.trim().toLowerCase();

  return EXCLUDED_AUTO_NUMBER_SERIALS.has(serialNumber) || remark === "test toestel" || hardwareBoiler === "test-";
};

const getLatestProductionSerial = (serials) => {
  if (!Array.isArray(serials) || serials.length === 0) return null;
  return serials.find((serialItem) => !isExcludedFromAutoNumbering(serialItem)) || serials[0];
};

const getLatestOptionValue = (options) => {
  if (!Array.isArray(options) || options.length === 0) return null;
  return getLatestOption(options);
};

const formatDeliveryTerm = (deliveryTerm) => {
  if (!deliveryTerm) return "Unknown";
  if (String(deliveryTerm) === "TBD-WEBSHOP") return deliveryTerm;
  return String(deliveryTerm).substring(0, 10);
};

const getDateSortValue = (value) => {
  if (!value) return Number.MAX_SAFE_INTEGER;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? Number.MAX_SAFE_INTEGER : date.getTime();
};

const getDeliveryTermSortGroup = (order) => {
  const deliveryTerm = String(order?.Leveringstermijn || "").trim();
  if (!deliveryTerm) return 2;
  if (!order?.parsed_delivery_term && !order?.parsedDeliveryTerm) return 0;
  return 1;
};

const sortOpenMachineOrdersByDueDate = (orders) =>
  [...orders].sort((a, b) => {
    const groupDiff = getDeliveryTermSortGroup(a) - getDeliveryTermSortGroup(b);
    if (groupDiff !== 0) return groupDiff;

    if (getDeliveryTermSortGroup(a) === 0) {
      const deliveryTermDiff = String(a.Leveringstermijn || "").localeCompare(String(b.Leveringstermijn || ""));
      if (deliveryTermDiff !== 0) return deliveryTermDiff;
    }

    const dueDateDiff = getDateSortValue(a.parsed_delivery_term || a.parsedDeliveryTerm) - getDateSortValue(b.parsed_delivery_term || b.parsedDeliveryTerm);
    if (dueDateDiff !== 0) return dueDateDiff;

    return Number(b.OrderbevestigingID) - Number(a.OrderbevestigingID);
  });

const groupOpenMachineLinesByOrder = (openMachineLines = []) => {
  const orderMap = new Map();

  openMachineLines.forEach((line) => {
    const orderId = String(line.OrderbevestigingID);
    if (!orderMap.has(orderId)) {
      orderMap.set(orderId, {
        OrderbevestigingID: line.OrderbevestigingID,
        Orderbevestigingnummer: line.Orderbevestigingnummer,
        NAAM: line.NAAM,
        Leveringstermijn: line.Leveringstermijn,
        parsed_delivery_term: line.parsed_delivery_term,
        parsedDeliveryTerm: line.parsedDeliveryTerm,
        RefKlant: line.RefKlant,
        Notities: line.Notities,
        lines: [],
      });
    }

    orderMap.get(orderId).lines.push(line);
  });

  const orders = Array.from(orderMap.values()).map((order) => ({
    ...order,
    lines: [...order.lines].sort((a, b) => Number(a.OrderbevestigingLijnID) - Number(b.OrderbevestigingLijnID) || Number(a.slot_index) - Number(b.slot_index)),
  }));

  return sortOpenMachineOrdersByDueDate(orders);
};

export const SerialsModal = ({ modalActive, closeModal, isEditSerial, isRFID, serialToEdit, setSerialToast, setLabelToast }) => {
  const [serial, setSerial] = useState(serialToEdit || initialSerial);
  const [modelString, setModelString] = useState({});
  const [modelStringTouched, setModelStringTouched] = useState(false);
  const [nextSerialnumber, setNextSerialnumber] = useState();
  const [printLabel, setPrintLabel] = useState(initialLabel);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [savingResult, setSavingResult] = useState(false);
  const [rfidCreateMode, setRfidCreateMode] = useState(isRFID ? RFID_CREATE_MODES.ADD_NEW : null);
  const [serialError, setSerialError] = useState(false);
  const [openMachineLines, setOpenMachineLines] = useState([]);
  const [selectedOpenMachineOrderId, setSelectedOpenMachineOrderId] = useState(null);
  const [openOrderLineError, setOpenOrderLineError] = useState(false);

  const user = useAuthStore();
  const users = useLookupStore((state) => state.users);
  const producttypes = useLookupStore((state) => state.producttypes);
  const recipetypes = useLookupStore((state) => state.recipetypes);
  const softwaretypes = useLookupStore((state) => state.softwaretypes);
  const hardwaretypes = useLookupStore((state) => state.hardwaretypes);
  const hardwareboilertypes = useLookupStore((state) => state.hardwareboilertypes);

  const userOptions = getOptions(
    users.filter((u) => !u.Inactief),
    "GebruikerID",
    "Afkorting"
  );
  const productOptions = getOptions(producttypes, "ProductTypeID", "Product");
  const recipeOptions = getOptions(recipetypes, "RecipeTypeID", "Recipe");
  const softwareOptions = getOptions(softwaretypes, "SofwareTypeID", "Software");
  const hardwareOptions = getOptions(hardwaretypes, "HardwareTypeID", "Hardware");
  const hardwareBoilerOptions = getOptions(hardwareboilertypes, "HardwareBoilerTypeID", "HardwareBoiler");

  const creatingNewSerial = isRFID ? rfidCreateMode !== RFID_CREATE_MODES.EXISTING : !isEditSerial;
  const creatingFromOpenOrderLine = isRFID && rfidCreateMode === RFID_CREATE_MODES.OPEN_ORDER_LINES;
  const creatingFromScratch = isRFID && rfidCreateMode === RFID_CREATE_MODES.ADD_NEW;

  const getDefaultSerialDraft = (serialNumber, remark = "") => ({
    ...initialSerial,
    serialnumber: serialNumber,
    remarks: remark,
    softwareTypeID: getLatestOptionValue(softwareOptions),
    recipeTypeID: getLatestOptionValue(recipeOptions),
    productTypeID: getLatestOptionValue(productOptions),
    hardwareTypeID: getLatestOptionValue(hardwareOptions),
    hardwareBoilerTypeID: getLatestOptionValue(hardwareBoilerOptions),
    qualityCheckByUserID: userOptions.find((u) => Number(u.value) === Number(user?.user?.id))?.value || null,
  });

  const openMachineOrders = useMemo(() => groupOpenMachineLinesByOrder(openMachineLines), [openMachineLines]);

  const openMachineOrderOptions = openMachineOrders.map((order) => ({
    value: String(order.OrderbevestigingID),
    label: `#${order.Orderbevestigingnummer} | ${order.NAAM} | ${formatDeliveryTerm(order.Leveringstermijn)} | ${order.lines.length} serial${order.lines.length === 1 ? "" : "s"}`,
  }));

  const selectedOpenMachineOrder = openMachineOrders.find((order) => String(order.OrderbevestigingID) === String(selectedOpenMachineOrderId)) || null;

  const getOpenOrderSerialPreview = (line, index) => {
    const previewSerialNumber = (Number(nextSerialnumber) || 0) + index;
    const lineModelString = parseModelString(line.config_string) || {};
    const remarks = saveStringInRemark(lineModelString, "") || "";
    const labelDetails = getPrintLabelDetailsFromModelString(lineModelString);

    return {
      line,
      serialnumber: previewSerialNumber,
      modelString: lineModelString,
      remarks,
      isValid: Boolean(previewSerialNumber && remarks && labelDetails.Model),
      printLabel: {
        ...printLabel,
        SerialNr: previewSerialNumber,
        ...labelDetails,
      },
    };
  };

  const selectedOpenMachineOrderSerials = useMemo(() => {
    if (!selectedOpenMachineOrder || !nextSerialnumber) return [];
    return selectedOpenMachineOrder.lines.map((line, index) => getOpenOrderSerialPreview(line, index));
  }, [selectedOpenMachineOrder, nextSerialnumber, printLabel]);

  const selectedOrderHasInvalidSerials = selectedOpenMachineOrderSerials.some((item) => !item.isValid);

  useEffect(() => {
    let cancelled = false;

    async function initializeModal() {
      setDataLoaded(false);
      setSerialError(false);
      setOpenOrderLineError(false);
      setModelStringTouched(false);

      let latestSerial = null;
      if (isRFID || creatingNewSerial) {
        const latestSerials = await getRequest(`/api/serials?limit=25`);
        latestSerial = getLatestProductionSerial(latestSerials);
        if (cancelled) return;
      }

      if (!isRFID) {
        if (creatingNewSerial) {
          const nextSerial = (Number(latestSerial?.serialnumber) || 0) + 1;
          const latestCodeString = getCodeString(latestSerial?.remarks) || "";

          setNextSerialnumber(nextSerial);
          setSerial(getDefaultSerialDraft(nextSerial, latestCodeString));
          setModelString(parseModelString(latestCodeString) || {});
        } else {
          const existingCodeString = getCodeString(serialToEdit?.remarks) || serialToEdit?.remarks || "";

          setSerial(serialToEdit || initialSerial);
          setModelString(parseModelString(existingCodeString) || {});
        }

        setOpenMachineLines([]);
        setSelectedOpenMachineOrderId(null);
        setDataLoaded(true);
        return;
      }

      if (creatingNewSerial) {
        const nextSerial = (Number(latestSerial?.serialnumber) || 0) + 1;
        const latestCodeString = getCodeString(latestSerial?.remarks) || "";
        let nextModelString = creatingFromScratch ? parseModelString(latestCodeString) || {} : {};
        let nextSerialDraft = getDefaultSerialDraft(nextSerial, creatingFromScratch ? latestCodeString : "");
        let nextOpenMachineLines = [];
        let nextSelectedOpenMachineOrderId = null;

        if (creatingFromOpenOrderLine) {
          const openMachineLineData = await getRequest("/api/fulfilment/open-machine-lines");
          if (cancelled) return;

          nextOpenMachineLines = Array.isArray(openMachineLineData) ? openMachineLineData : [];
          const firstOpenMachineOrder = groupOpenMachineLinesByOrder(nextOpenMachineLines)[0];
          nextSelectedOpenMachineOrderId = firstOpenMachineOrder?.OrderbevestigingID ? String(firstOpenMachineOrder.OrderbevestigingID) : null;
        }

        setNextSerialnumber(nextSerial);
        setOpenMachineLines(nextOpenMachineLines);
        setSelectedOpenMachineOrderId(nextSelectedOpenMachineOrderId);
        setSerial(nextSerialDraft);
        setModelString(nextModelString);
        setPrintLabel((label) => ({
          ...label,
          SerialNr: nextSerial,
          ...getPrintLabelDetailsFromModelString(nextModelString),
        }));
      } else {
        const latestExistingModelString = parseModelString(getCodeString(latestSerial?.remarks)) || {};

        setOpenMachineLines([]);
        setSelectedOpenMachineOrderId(null);
        setSerial(latestSerial || initialSerial);
        setModelString(latestExistingModelString);
        setPrintLabel((label) => ({
          ...label,
          SerialNr: latestSerial?.serialnumber || null,
          ...getPrintLabelDetailsFromModelString(latestExistingModelString),
        }));
      }

      setDataLoaded(true);
    }

    initializeModal();

    return () => {
      cancelled = true;
    };
  }, [
    creatingFromOpenOrderLine,
    creatingFromScratch,
    creatingNewSerial,
    isRFID,
    rfidCreateMode,
    serialToEdit,
    user?.user?.id,
    userOptions.length,
    productOptions.length,
    recipeOptions.length,
    softwareOptions.length,
    hardwareOptions.length,
    hardwareBoilerOptions.length,
  ]);

  useEffect(() => {
    if (!isRFID || !modelString) return;

    setPrintLabel((label) => ({
      ...label,
      ...getPrintLabelDetailsFromModelString(modelString),
    }));
  }, [isRFID, modelString]);

  const handleChange = (value, id) => {
    setSerial((currentSerial) => ({
      ...currentSerial,
      [id]: value,
    }));
  };

  const handleOpenMachineOrderChange = (orderId) => {
    setSelectedOpenMachineOrderId(orderId);
    setOpenOrderLineError(false);
  };

  const handlePrintLabelChange = async (value, id) => {
    if (id === "SerialNr" && value) {
      const data = await getRequest(`/api/serials/${value}`);
      if (data) {
        setSerial(data);
        setModelString(parseModelString(getCodeString(data.remarks)) || {});
      }
    }

    setPrintLabel((currentLabel) => ({
      ...currentLabel,
      [id]: value,
    }));
  };

  const handleSave = async () => {
    setSavingResult(true);

    if (creatingFromOpenOrderLine && !selectedOpenMachineOrder) {
      setOpenOrderLineError(true);
      setSavingResult(false);
      return;
    }

    if (creatingFromOpenOrderLine) {
      if (selectedOpenMachineOrderSerials.length === 0 || selectedOrderHasInvalidSerials) {
        setSerialError(true);
        setSavingResult(false);
        return;
      }

      let requestSucceeded = true;

      for (let i = 0; i < selectedOpenMachineOrderSerials.length; i++) {
        const serialPreview = selectedOpenMachineOrderSerials[i];
        const serialToPersist = {
          ...getDefaultSerialDraft(serialPreview.serialnumber, serialPreview.remarks),
          remarks: serialPreview.remarks,
        };

        requestSucceeded = Boolean(await postRequest("/api/serials", serialToPersist));
        if (!requestSucceeded) break;

        requestSucceeded = Boolean(await postRequest(`/api/sales/${selectedOpenMachineOrder.OrderbevestigingID}/serials`, { serial_id: serialToPersist.serialnumber }));
        if (!requestSucceeded) break;
      }

      if (requestSucceeded) {
        for (let i = 0; i < selectedOpenMachineOrderSerials.length; i++) {
          requestSucceeded = Boolean(await postRequest("/api/rfid_events/printlabel", selectedOpenMachineOrderSerials[i].printLabel));
          if (!requestSucceeded) break;
        }
      }

      if (requestSucceeded) {
        setSerialError(false);
        setSerialToast("ok");
        setLabelToast("ok");
        closeModal();
      } else {
        setSerialToast("error");
        setLabelToast("error");
      }

      setSavingResult(false);
      return;
    }

    const serialToPersist = {
      ...serial,
      remarks: saveStringInRemark(modelString, serial.remarks),
    };

    if (!serialToPersist.remarks) {
      setSerialError(true);
      setSavingResult(false);
      return;
    }

    setSerial(serialToPersist);

    let requestSucceeded = true;

    if (creatingNewSerial) requestSucceeded = Boolean(await postRequest("/api/serials", serialToPersist, setSerialToast));

    if (requestSucceeded && ((!creatingNewSerial && !isRFID) || modelStringTouched)) {
      requestSucceeded = Boolean(await putRequest(`/api/serials/${serialToPersist.serialnumber}`, serialToPersist, setSerialToast));
    }

    if (requestSucceeded && isRFID) {
      requestSucceeded = Boolean(await postRequest("/api/rfid_events/printlabel", { ...printLabel, SerialNr: serialToPersist.serialnumber }, setLabelToast));
    }

    if (requestSucceeded) {
      setSerialError(false);
      closeModal();
    }

    setSavingResult(false);
  };

  return (
    <Modal
      open={modalActive}
      onClose={closeModal}
      size="large"
      loading={!dataLoaded}
      title={isRFID ? "Create RFID print label" : `${creatingNewSerial ? "Add" : "Edit"} serial number ${dataLoaded ? "#" + serial.serialnumber : ""}`}
      primaryAction={{
        content: creatingFromOpenOrderLine
          ? selectedOpenMachineOrderSerials.length
            ? `Create and print ${selectedOpenMachineOrderSerials.length} label${selectedOpenMachineOrderSerials.length === 1 ? "" : "s"}`
            : "Create and print labels"
          : "Save",
        onAction: handleSave,
        loading: savingResult,
        disabled: creatingFromOpenOrderLine && (openMachineLines.length === 0 || !selectedOpenMachineOrder || selectedOrderHasInvalidSerials),
      }}
      secondaryActions={[{ content: "Cancel", onAction: closeModal }]}>
      {isRFID ? (
        <Modal.Section>
          <ButtonGroup variant="segmented" fullWidth>
            <Button size="large" pressed={rfidCreateMode === RFID_CREATE_MODES.ADD_NEW} onClick={() => setRfidCreateMode(RFID_CREATE_MODES.ADD_NEW)}>
              {`Add new serial number${nextSerialnumber ? ` #${nextSerialnumber}` : ""}`}
            </Button>
            <Button size="large" pressed={rfidCreateMode === RFID_CREATE_MODES.OPEN_ORDER_LINES} onClick={() => setRfidCreateMode(RFID_CREATE_MODES.OPEN_ORDER_LINES)}>
              Form orders
            </Button>
            <Button size="large" pressed={rfidCreateMode === RFID_CREATE_MODES.EXISTING} onClick={() => setRfidCreateMode(RFID_CREATE_MODES.EXISTING)}>
              Create label for existing serial number
            </Button>
          </ButtonGroup>
        </Modal.Section>
      ) : null}

      <Modal.Section>
        <Form implicitSubmit onSubmit={handleSave}>
          {(isRFID && creatingNewSerial) || !isRFID ? (
            <FormLayout>
              {creatingFromOpenOrderLine ? (
                <>
                  <FormLayout.Group>
                    <Select id="openMachineOrder" label="Order (sorted by due date)" options={openMachineOrderOptions} value={selectedOpenMachineOrderId || ""} onChange={handleOpenMachineOrderChange} />
                    <TextField id="ManufactureYear" label="Manufacture year on print label" value={printLabel.ManufactureYear} onChange={handlePrintLabelChange} />
                  </FormLayout.Group>

                  {openMachineLines.length === 0 ? (
                    <Banner tone="info">No open machine order lines are available right now. You can still use the other RFID label routes.</Banner>
                  ) : null}

                  {openOrderLineError ? <div style={{ marginTop: "-8px", color: "red" }}>Select an open order first</div> : null}
                  {serialError || selectedOrderHasInvalidSerials ? <div style={{ marginTop: "-8px", color: "red" }}>One or more machine lines do not have a valid model string.</div> : null}

                  {selectedOpenMachineOrder ? (
                    <div style={{ padding: "12px", border: "1px solid #dfe3e8", borderRadius: "8px", marginBottom: "8px" }}>
                      <BlockStack gap="200">
                        <InlineStack gap="200" blockAlign="center" wrap>
                          <Link removeUnderline url={`/sales/${selectedOpenMachineOrder.OrderbevestigingID}`}>
                            {`Order #${selectedOpenMachineOrder.Orderbevestigingnummer}`}
                          </Link>
                          <Badge>{selectedOpenMachineOrder.NAAM}</Badge>
                          <Badge>{`Delivery ${formatDeliveryTerm(selectedOpenMachineOrder.Leveringstermijn)}`}</Badge>
                          <Badge>{`${selectedOpenMachineOrderSerials.length} serial${selectedOpenMachineOrderSerials.length === 1 ? "" : "s"}`}</Badge>
                        </InlineStack>
                        {selectedOpenMachineOrder.RefKlant ? <Text>{`Customer reference: ${selectedOpenMachineOrder.RefKlant}`}</Text> : null}
                        {selectedOpenMachineOrder.Notities ? <Text>{`Order note: ${selectedOpenMachineOrder.Notities}`}</Text> : null}
                        <Box paddingBlockStart="200">
                          <DataTable
                            columnContentTypes={["numeric", "text", "text", "text", "text"]}
                            headings={["Serial", "Machine", "Product", "Order line", "Status"]}
                            rows={selectedOpenMachineOrderSerials.map((item) => [
                              item.serialnumber,
                              <Badge tone={item.isValid ? undefined : "critical"}>{item.line.config_string || "Invalid"}</Badge>,
                              item.line.ProductNameNL || "",
                              item.line.remaining_line_slots > 1 ? `Slot ${item.line.slot_index}/${item.line.remaining_line_slots}` : item.line.tekstlijn,
                              item.isValid ? "Ready to create and print" : "Invalid model string",
                            ])}
                          />
                        </Box>
                      </BlockStack>
                    </div>
                  ) : null}
                </>
              ) : null}

              {!creatingFromOpenOrderLine ? (
                <>
                  <FormLayout.Group>
                    <ModelString modelString={modelString} setModelString={setModelString} printLabel={printLabel} setPrintLabel={setPrintLabel} disabled={false} />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <Select id="recipeTypeID" label="Recipe" options={recipeOptions} onChange={handleChange} value={serial.recipeTypeID?.toString()} />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <Select id="hardwareTypeID" label="Hardware" options={hardwareOptions} onChange={handleChange} value={serial.hardwareTypeID?.toString()} />
                    <Select id="hardwareBoilerTypeID" label="Hardware boiler" options={hardwareBoilerOptions} value={serial.hardwareBoilerTypeID?.toString()} onChange={handleChange} />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <Select id="softwareTypeID" label="Software" options={softwareOptions} onChange={handleChange} value={serial.softwareTypeID?.toString()} />
                    <Select id="Status" label="Status" options={statusOptions} onChange={handleChange} value={serial.Status} />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <TextField id="qualityCheckDate" label="Quality check date" type="date" value={serial.qualityCheckDate} onChange={handleChange} />
                    <Select
                      id="qualityCheckByUserID"
                      label="Quality check by user"
                      options={userOptions}
                      onChange={handleChange}
                      value={serial.qualityCheckByUserID?.toString() || user?.user?.id?.toString() || ""}
                      placeholder="Select user..."
                    />
                  </FormLayout.Group>
                  <TextField id="RfidLocation" label="RFID STATION" maxLength={50} value={serial.RfidLocation || ""} onChange={handleChange} autoComplete="off" />
                  <FormLayout.Group>
                    <TextField id="remarks" label="Remarks" maxLength={500} multiline={10} showCharacterCount value={serial.remarks || ""} onChange={handleChange} autoComplete="off" />
                  </FormLayout.Group>

                  {isRFID ? (
                    <FormLayout.Group>
                      <TextField id="ManufactureYear" label="Manufacture year on print label" value={printLabel.ManufactureYear} onChange={handlePrintLabelChange} />
                    </FormLayout.Group>
                  ) : null}
                </>
              ) : null}
            </FormLayout>
          ) : isRFID && !creatingNewSerial ? (
            <FormLayout>
              <FormLayout.Group>
                <SerialSelect id="SerialNr" value={printLabel.SerialNr} onChange={handlePrintLabelChange} label="Serial number" />
                <TextField id="ManufactureYear" label="Manufacture year on print label" value={printLabel.ManufactureYear} onChange={handlePrintLabelChange} />
              </FormLayout.Group>
              <FormLayout.Group>
                <ModelString
                  modelString={modelString}
                  setModelString={setModelString}
                  printLabel={printLabel}
                  setPrintLabel={setPrintLabel}
                  updateSerialString={true}
                  serialError={serialError}
                  setModelStringTouched={setModelStringTouched}
                />
              </FormLayout.Group>
            </FormLayout>
          ) : null}
        </Form>
      </Modal.Section>
    </Modal>
  );
};
